import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Bell, CreditCard, Shield, TrendingUp, CheckCheck, Star, Gift, AlertTriangle } from 'lucide-react';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => { fetchNotifications(); }, []);

  const fetchNotifications = async () => {
    try {
      const res = await notificationApi.getAll(50);
      setNotifications(res.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationApi.markRead(id);
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    } catch (error) {}
  };

  const markAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'transaction': return CreditCard;
      case 'campaign': return TrendingUp;
      case 'security': return Shield;
      case 'tip': return Star;
      case 'promo': return Gift;
      case 'payout': return CreditCard;
      default: return Bell;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'transaction': return 'bg-green-500/20 text-green-500';
      case 'campaign': return 'bg-primary/20 text-primary';
      case 'security': return 'bg-yellow-500/20 text-yellow-500';
      case 'tip': return 'bg-purple-500/20 text-purple-500';
      case 'promo': return 'bg-blue-500/20 text-blue-500';
      case 'payout': return 'bg-green-500/20 text-green-500';
      default: return 'bg-white/10 text-muted-foreground';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8 lg:max-w-3xl lg:mx-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 border-b border-white/10 lg:border-0 lg:bg-transparent lg:backdrop-blur-none lg:static">
          <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
            <h1 className="text-xl lg:text-3xl font-bold flex-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h1>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-2 text-sm text-primary hover:underline" data-testid="mark-all-read">
                <CheckCheck className="w-4 h-4" />Mark all read
              </button>
            )}
          </div>
        </div>

        <div className="px-6 lg:px-0">
          {isLoading ? (
            <div className="space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto rounded-full bg-white/5 flex items-center justify-center mb-4"><Bell className="w-8 h-8 text-muted-foreground" /></div>
              <h3 className="font-semibold mb-2">You're all caught up</h3>
              <p className="text-muted-foreground">No new notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const Icon = getIcon(notification.type);
                const iconColor = getIconColor(notification.type);
                return (
                  <button
                    key={notification.notification_id}
                    onClick={() => markAsRead(notification.notification_id)}
                    className={`w-full text-left bg-card border rounded-xl p-4 transition-colors ${notification.read ? 'border-white/10' : 'border-primary/30 bg-primary/5'}`}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full ${iconColor} flex items-center justify-center flex-shrink-0`}><Icon className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{notification.title}</p>
                          {!notification.read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{notification.body}</p>
                        <p className="text-xs text-muted-foreground mt-2">{formatRelativeTime(notification.created_at)}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default NotificationsPage;
